import React, { useState, useEffect, useRef } from 'react';
import VirtualKeyboard from './VirtualKeyboard';
import metricsTracker from '../utils/metricsTracker';

const SubtractionExam = ({ updateMetrics }) => {
  // Exam state
  const [examQuestions, setExamQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionsPerPage, setQuestionsPerPage] = useState(20);
  const [examStarted, setExamStarted] = useState(false);
  const [examCompleted, setExamCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [examProgress, setExamProgress] = useState({});
  const [examConfig, setExamConfig] = useState({
    questionCount: 2,
    gradeLevel: '1',
    operation: 'subtraction',
    useTimer: false,
    timeLimit: 30
  });
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  // Virtual keyboard state
  const [keyboardVisible, setKeyboardVisible] = useState(true);
  const [keyboardPosition, setKeyboardPosition] = useState({ x: 400, y: 100 });
  const [activeInput, setActiveInput] = useState(null);
  const mathProblemRefs = useRef({});

  // Level difficulty ranges - properly aligned with "Upto X digits"
  const gradeRanges = {
    '1': { min: 1, max: 99 },        // Upto 2 digits
    '2': { min: 10, max: 999 },      // Upto 3 digits
    '3': { min: 100, max: 9999 },    // Upto 4 digits
    '4': { min: 1000, max: 99999 },  // Upto 5 digits
    '5': { min: 10000, max: 999999 }, // Upto 6 digits
    '6': { min: 100000, max: 9999999 }, // Upto 7 digits
    '7': { min: 1000000, max: 99999999 }, // Upto 8 digits
    '8': { min: 10000000, max: 999999999 } // Upto 9 digits
  };

  // Calculate paper savings
  const calculatePaperSavings = () => {
    const linesPerQuestion = 3;
    const validTotalQuestions = Number.isFinite(totalQuestions) ? totalQuestions : 0;
    const totalLines = validTotalQuestions * linesPerQuestion;
    const papersSaved = Math.ceil(totalLines / 25);
    return { papersSaved, totalLines };
  };

  // Timer functions
  const formatTime = (seconds) => {
    // Handle invalid input
    if (!seconds || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    const totalSeconds = examConfig.timeLimit * 60;
    setTimeRemaining(totalSeconds);
    setTimerActive(true);
  };

  const stopTimer = () => {
    setTimerActive(false);
  };

  // Generate exam questions for subtraction
  const generateExam = () => {
    const questions = [];
    const range = gradeRanges[examConfig.gradeLevel];
    
    for (let i = 0; i < examConfig.questionCount; i++) {
      const num1 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      const num2 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      
      // Ensure num1 >= num2 for valid subtraction
      const largerNum = Math.max(num1, num2);
      const smallerNum = Math.min(num1, num2);
      const correctAnswer = largerNum - smallerNum;
      
      questions.push({
        id: i + 1,
        num1: largerNum.toString(),
        num2: smallerNum.toString(),
        correctAnswer: correctAnswer.toString(),
        userAnswer: '',
        afterReduction: '',
        afterBorrowing: '',
        strikeout: '',
        isCorrect: null,
        explanation: generateExplanation(largerNum, smallerNum, correctAnswer)
      });
    }
    
    setExamQuestions(questions);
    setTotalQuestions(examConfig.questionCount);
    setCurrentPage(1);
    setExamStarted(true);
    setExamCompleted(false);
    setShowResults(false);
    setExamProgress({});
    
    // Track exam started
    metricsTracker.trackExamStarted('subtraction', examConfig.gradeLevel, examConfig.questionCount);
    
    // Start timer if enabled
    if (examConfig.useTimer) {
      startTimer();
    }
    
    // Report metrics for new exam
    if (updateMetrics) {
      const { papersSaved } = calculatePaperSavings();
      updateMetrics(papersSaved);
    }
  };

  // Generate step-by-step explanation for subtraction
  const generateExplanation = (num1, num2, result) => {
    const aDigits = num1.toString().split('').reverse();
    const bDigits = num2.toString().split('').reverse();
    const maxLength = Math.max(aDigits.length, bDigits.length);
    
    let steps = [];
    let borrow = 0;
    
    for (let i = 0; i < maxLength; i++) {
      const digitA = parseInt(aDigits[i] || 0);
      const digitB = parseInt(bDigits[i] || 0);
      
      // Apply borrowing if needed
      let currentDigitA = digitA - borrow;
      let newBorrow = 0;
      let needsBorrow = false;
      let explanation = '';
      
      if (currentDigitA < digitB) {
        needsBorrow = true;
        currentDigitA += 10;
        newBorrow = 1;
        explanation = `Since ${currentDigitA - 10} < ${digitB}, we need to borrow 1 from the next place value. ${currentDigitA - 10} + 10 = ${currentDigitA}`;
      } else {
        explanation = `Since ${currentDigitA} ≥ ${digitB}, we can subtract directly.`;
      }
      
      const resultDigit = currentDigitA - digitB;
      
      let placeValue = '';
      if (i === 0) placeValue = 'ones';
      else if (i === 1) placeValue = 'tens';
      else if (i === 2) placeValue = 'hundreds';
      else if (i === 3) placeValue = 'thousands';
      else if (i === 4) placeValue = 'ten thousands';
      else if (i === 5) placeValue = 'hundred thousands';
      else if (i === 6) placeValue = 'millions';
      else placeValue = `${Math.pow(10, i)}s`;
      
      steps.push({
        position: i + 1,
        digitA,
        digitB,
        borrow: borrow,
        currentDigitA,
        resultDigit,
        newBorrow: newBorrow,
        placeValue,
        needsBorrow,
        explanation,
        equation: `${currentDigitA} - ${digitB} = ${resultDigit}`
      });
      
      borrow = newBorrow;
    }
    
    return { steps, result };
  };

  // Handle answer input changes
  const handleAnswerChange = (questionId, index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const updatedQuestions = examQuestions.map(q => {
        if (q.id === questionId) {
          const newAnswer = [...(q.userAnswer || [])];
          newAnswer[index] = value;
          return { ...q, userAnswer: newAnswer };
        }
        return q;
      });
      setExamQuestions(updatedQuestions);
      saveExamToStorage(updatedQuestions);
    }
  };

  // Handle after reduction input changes
  const handleAfterReductionChange = (questionId, index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const updatedQuestions = examQuestions.map(q => {
        if (q.id === questionId) {
          const newAfterReduction = [...(q.afterReduction || [])];
          newAfterReduction[index] = value;
          return { ...q, afterReduction: newAfterReduction };
        }
        return q;
      });
      setExamQuestions(updatedQuestions);
      saveExamToStorage(updatedQuestions);
    }
  };

  // Handle after borrowing input changes
  const handleAfterBorrowingChange = (questionId, index, value) => {
    if (value.length <= 2 && /^\d*$/.test(value)) {
      const updatedQuestions = examQuestions.map(q => {
        if (q.id === questionId) {
          const newAfterBorrowing = [...(q.afterBorrowing || [])];
          newAfterBorrowing[index] = value;
          return { ...q, afterBorrowing: newAfterBorrowing };
        }
        return q;
      });
      setExamQuestions(updatedQuestions);
      saveExamToStorage(updatedQuestions);
    }
  };

  // Handle strikeout input changes
  const handleStrikeoutChange = (questionId, index, value) => {
    if (value === '/' || value === '') {
      const updatedQuestions = examQuestions.map(q => {
        if (q.id === questionId) {
          const newStrikeout = [...(q.strikeout || [])];
          newStrikeout[index] = value;
          return { ...q, strikeout: newStrikeout };
        }
        return q;
      });
      setExamQuestions(updatedQuestions);
      saveExamToStorage(updatedQuestions);
    }
  };

  // Submit exam
  const submitExam = () => {
    const updatedQuestions = examQuestions.map(q => ({
      ...q,
      isCorrect: (q.userAnswer || []).join('') === q.correctAnswer
    }));
    
    // Calculate score
    const correctAnswers = updatedQuestions.filter(q => q.isCorrect).length;
    const score = Math.round((correctAnswers / updatedQuestions.length) * 100);
    
    setExamQuestions(updatedQuestions);
    setExamCompleted(true);
    setShowResults(true);
    stopTimer();
    saveExamToStorage(updatedQuestions);
    
    // Track exam submitted
    metricsTracker.trackExamSubmitted('subtraction', examConfig.gradeLevel, score, updatedQuestions.length, correctAnswers);
    
    // Scroll to top to show Exam Results Summary
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Print exam with solutions
  const printExam = () => {
    // Track print workbook
    metricsTracker.trackPrintWorkbook('subtraction', examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>BellMaths Subtraction Exam</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              background-color: #f5f5f5;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .header h1 {
              color: #667eea;
              margin: 0 0 10px 0;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .question { 
              margin-bottom: 40px; 
              page-break-inside: avoid; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .question-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e9ecef;
            }
            .question-header h3 {
              margin: 0;
              color: #667eea;
            }
            .math-problem {
              margin: 20px 0;
            }
            /* Grid layout for side-by-side question and solution */
            .question > div[style*="grid-template-columns"] {
              display: grid !important;
              grid-template-columns: 1fr 1fr !important;
              gap: 20px !important;
              align-items: start !important;
            }
            .problem-grid {
              display: grid;
              gap: 0px;
              align-items: center;
              margin-bottom: 20px;
            }
            .digit-input {
              width: 40px;
              height: 50px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f4fd;
            }
            .after-reduction-input {
              width: 30px;
              height: 25px;
              font-size: 0.8rem;
              text-align: center;
              border: none;
              border-bottom: 2px solid #0066cc;
              border-radius: 0;
              background-color: #e6f3ff;
              color: #0066cc;
              font-weight: bold;
            }
            .after-borrowing-input {
              width: 35px;
              height: 25px;
              font-size: 0.8rem;
              text-align: center;
              border: none;
              border-bottom: 2px solid #ff8c00;
              border-radius: 0;
              background-color: #ffe6cc;
              color: #ff8c00;
              font-weight: bold;
            }
            .operator {
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              color: #333;
            }
            .answer-line {
              height: 2px;
              background-color: #333;
              margin: 10px 0;
            }
            .answer-input {
              width: 20px;
              height: 30px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: none;
              border-radius: 0;
              background-color: transparent;
              outline: none;
              border-bottom: 2px solid #333;
            }
            .solution-section {
              background-color: #f8f9fa;
              padding: 15px;
              border-radius: 8px;
              border: 1px solid #e9ecef;
            }
            .explanation-steps {
              font-size: 14px;
            }
            .page-break {
              page-break-before: always;
            }
            @media print {
              body { background-color: white; }
              .question { box-shadow: none; border: 1px solid #ddd; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  // Generate print content
  const generatePrintContent = () => {
    let content = `
      <div class="header">
        <h1>BellMaths - Subtraction Exam</h1>
        <p>Grade Level: ${examConfig.gradeLevel} | Total Questions: ${totalQuestions}</p>
        <p>Date: ${new Date().toLocaleDateString()}</p>
        <p>Student Name: _________________</p>
        <p style="color: #28a745; font-style: italic;">🌱 Eco-Friendly: This digital exam saves paper and helps protect our environment!</p>
      </div>
    `;

    examQuestions.forEach((question, index) => {
      if (index > 0 && index % 5 === 0) {
        content += '<div class="page-break"></div>';
      }
      
      const maxLength = Math.max(question.num1.length, question.num2.length, question.correctAnswer.length);
      const padNumber = (num, length) => num.padStart(length, ' ');
      
      content += `
        <div class="question">
          <div class="question-header">
            <h3>Question ${question.id}</h3>
            ${question.isCorrect !== undefined ? `
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="font-size: 12px; font-weight: bold; color: #666;">Status:</span>
                ${question.isCorrect ? 
                  '<span style="color: #28a745; font-weight: bold;">✓ Correct</span>' : 
                  '<span style="color: #dc3545; font-weight: bold;">✗ Incorrect</span>'
                }
              </div>
            ` : ''}
          </div>
          
          <!-- Question Content - Side by Side Layout -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: start;">
            <!-- Math Problem Section -->
            <div class="math-problem">
              <div class="problem-grid" style="grid-template-columns: 40px repeat(${maxLength}, 40px) auto;">
                <!-- After Reduction row -->
                <div></div>
      `;
      
      // After Reduction inputs
      for (let i = 0; i < maxLength; i++) {
        if (i < maxLength - 1) {
          const userValue = question.afterReduction && question.afterReduction[i] ? question.afterReduction[i] : '';
          content += `<input type="text" class="after-reduction-input" value="${userValue}" readonly />`;
        } else {
          content += `<div></div>`;
        }
      }
      content += `<div style="font-size: 0.8rem; color: #0066cc; font-weight: bold;">After Reduction</div>`;
      
      // After Borrowing row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        const userValue = question.afterBorrowing && question.afterBorrowing[i] ? question.afterBorrowing[i] : '';
        content += `<input type="text" class="after-borrowing-input" value="${userValue}" readonly />`;
      }
      content += `<div style="font-size: 0.8rem; color: #ff8c00; font-weight: bold;">After Borrowing</div>`;
      
      // First number row
      content += `<div></div>`;
      const paddedNum1 = padNumber(question.num1, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum1[i] === ' ' ? '' : paddedNum1[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Second number row with operator
      content += `<div class="operator">-</div>`;
      const paddedNum2 = padNumber(question.num2, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum2[i] === ' ' ? '' : paddedNum2[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Line row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        content += `<div class="answer-line"></div>`;
      }
      content += `<div></div>`;
      
      // Answer row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        const userValue = question.userAnswer && question.userAnswer[i] ? question.userAnswer[i] : '';
        content += `<input type="text" class="answer-input" value="${userValue}" readonly />`;
      }
      content += `<div></div>`;
      
      content += `
              </div>
            </div>

            <!-- Step-by-Step Solution Section -->
            <div class="solution-section">
              <div style="margin-bottom: 10px;">
                <h4>Step-by-Step Solution:</h4>
              </div>
              <div class="explanation-steps" style="font-size: 14px;">
      `;
      
      // Add solution steps with enhanced explanations
      if (question.explanation && question.explanation.steps) {
        question.explanation.steps.forEach((step, stepIndex) => {
          content += `
                <div style="margin-bottom: 12px; padding: 10px; background-color: #f8f9fa; border-radius: 6px; border-left: 4px solid #667eea; font-size: 13px;">
                  <div style="margin-bottom: 6px;">
                    <strong style="color: #667eea; font-size: 14px;">Step ${stepIndex + 1}: ${step.placeValue} place</strong>
                  </div>
                  <div style="margin-bottom: 4px; font-family: monospace; font-size: 12px; color: #495057;">
                    <strong>Equation:</strong> ${step.equation}
                  </div>
                  <div style="margin-bottom: 4px; font-size: 12px; color: #6c757d;">
                    <strong>Explanation:</strong> ${step.explanation}
                  </div>
                  ${step.needsBorrow && `
                    <div style="margin-top: 4px; padding: 4px; background-color: #fff3cd; border-radius: 3px; font-size: 11px; color: #856404;">
                      <strong>Borrowing:</strong> We borrowed 1 from the next place value (${step.newBorrow} will be carried to the next step)
                    </div>
                  `}
                </div>
          `;
        });
      } else {
        content += `
                <div style="margin-bottom: 8px; padding: 6px; background-color: #fff3cd; border-radius: 4px; border: 1px solid #ffeaa7; font-size: 13px; color: #856404;">
                  <strong>Note:</strong> Step-by-step solution not available for this question.
                </div>
        `;
      }
      
      content += `
                <div style="margin-top: 8px; padding: 6px; background-color: #d4edda; border-radius: 4px; border: 1px solid #c3e6cb; text-align: center; font-weight: bold; color: #155724; font-size: 13px;">
                  <strong>Answer: ${question.correctAnswer}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    });

    return content;
  };

  // Generate print content for questions only (without solutions)
  const generateQuestionsOnlyContent = () => {
    let content = `
      <div class="header">
        <h1>BellMaths - Subtraction Exam</h1>
        <p>Grade Level: ${examConfig.gradeLevel} | Total Questions: ${totalQuestions}</p>
        <p>Date: ${new Date().toLocaleDateString()}</p>
        <p>Student Name: _________________</p>
        <p style="color: #28a745; font-style: italic;">🌱 Eco-Friendly: This digital exam saves paper and helps protect our environment!</p>
      </div>
    `;

    examQuestions.forEach((question, index) => {
      if (index > 0 && index % 5 === 0) {
        content += '<div class="page-break"></div>';
      }
      
      const maxLength = Math.max(question.num1.length, question.num2.length, question.correctAnswer.length);
      const padNumber = (num, length) => num.padStart(length, ' ');
      
      content += `
        <div class="question">
          <div class="question-header">
            <h3>Question ${question.id}</h3>
          </div>
          
          <!-- Math Problem Section -->
          <div class="math-problem">
            <div class="problem-grid" style="grid-template-columns: 40px repeat(${maxLength}, 40px) auto;">
              <!-- After Reduction row -->
              <div></div>
      `;
      
      // After Reduction inputs
      for (let i = 0; i < maxLength; i++) {
        if (i < maxLength - 1) {
          const userValue = question.afterReduction && question.afterReduction[i] ? question.afterReduction[i] : '';
          content += `<input type="text" class="after-reduction-input" value="${userValue}" readonly />`;
        } else {
          content += `<div></div>`;
        }
      }
      content += `<div style="font-size: 0.8rem; color: #0066cc; font-weight: bold;">After Reduction</div>`;
      
      // After Borrowing row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        const userValue = question.afterBorrowing && question.afterBorrowing[i] ? question.afterBorrowing[i] : '';
        content += `<input type="text" class="after-borrowing-input" value="${userValue}" readonly />`;
      }
      content += `<div style="font-size: 0.8rem; color: #ff8c00; font-weight: bold;">After Borrowing</div>`;
      
      // First number row
      content += `<div></div>`;
      const paddedNum1 = padNumber(question.num1, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum1[i] === ' ' ? '' : paddedNum1[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Second number row with operator
      content += `<div class="operator">-</div>`;
      const paddedNum2 = padNumber(question.num2, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum2[i] === ' ' ? '' : paddedNum2[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Line row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        content += `<div class="answer-line"></div>`;
      }
      content += `<div></div>`;
      
      // Answer row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        const userValue = question.userAnswer && question.userAnswer[i] ? question.userAnswer[i] : '';
        content += `<input type="text" class="answer-input" value="${userValue}" readonly />`;
      }
      content += `<div></div>`;
      
      content += `
            </div>
          </div>
        </div>
      `;
    });

    return content;
  };

  // Print questions only (without solutions)
  const printQuestionsOnly = () => {
    // Track print questions
    metricsTracker.trackPrintQuestions('subtraction', examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    const printContent = generateQuestionsOnlyContent();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>BellMaths Subtraction Exam - Questions Only</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              background-color: #f5f5f5;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .header h1 {
              color: #667eea;
              margin: 0 0 10px 0;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .question { 
              margin-bottom: 40px; 
              page-break-inside: avoid; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .question-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e9ecef;
            }
            .question-header h3 {
              margin: 0;
              color: #667eea;
            }
            .math-problem {
              margin: 20px 0;
            }
            .problem-grid {
              display: grid;
              gap: 0px;
              align-items: center;
              margin-bottom: 20px;
            }
            .digit-input {
              width: 40px;
              height: 50px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f4fd;
            }
            .after-reduction-input {
              width: 30px;
              height: 25px;
              font-size: 0.8rem;
              text-align: center;
              border: none;
              border-bottom: 2px solid #0066cc;
              border-radius: 0;
              background-color: #e6f3ff;
              color: #0066cc;
              font-weight: bold;
            }
            .after-borrowing-input {
              width: 35px;
              height: 25px;
              font-size: 0.8rem;
              text-align: center;
              border: none;
              border-bottom: 2px solid #ff8c00;
              border-radius: 0;
              background-color: #ffe6cc;
              color: #ff8c00;
              font-weight: bold;
            }
            .operator {
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              color: #333;
            }
            .answer-line {
              height: 2px;
              background-color: #333;
              margin: 10px 0;
            }
            .answer-input {
              width: 20px;
              height: 30px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: none;
              border-radius: 0;
              background-color: transparent;
              outline: none;
              border-bottom: 2px solid #333;
            }
            .page-break {
              page-break-before: always;
            }
            @media print {
              body { background-color: white; }
              .question { box-shadow: none; border: 1px solid #ddd; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  // Print plain questions (simple format without BellMaths UI design)
  const printPlainQuestions = () => {
    // Track print answers
    metricsTracker.trackPrintAnswers('subtraction', examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    
    let plainContent = `
      <html>
        <head>
          <title>Math Exam - Questions</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .header h1 {
              margin: 0 0 10px 0;
              color: #333;
            }
            .question { 
              margin-bottom: 30px; 
              page-break-inside: avoid;
            }
            .question-number {
              font-weight: bold;
              margin-bottom: 10px;
              color: #333;
            }
            .math-problem {
              font-size: 18px;
              margin: 15px 0;
              padding: 15px;
              border: 1px solid #ddd;
              background-color: #f9f9f9;
            }
            .answer-space {
              margin-top: 20px;
              padding: 10px;
              border: 1px solid #ccc;
              background-color: #fff;
              min-height: 40px;
            }
            @media print {
              body { margin: 15px; }
              .question { margin-bottom: 25px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Math Exam</h1>
            <p>Student Name: _________________</p>
            <p>Date: _________________</p>
            <p>Total Questions: ${examQuestions.length}</p>
          </div>
    `;

    examQuestions.forEach((question, index) => {
      plainContent += `
        <div class="question">
          <div class="question-number">Question ${index + 1}</div>
          <div class="math-problem">
            ${question.num1} - ${question.num2} = ?
          </div>
          <div class="answer-space">
            Answer: _________________
          </div>
        </div>
      `;
    });

    plainContent += `
        </body>
      </html>
    `;
    
    printWindow.document.write(plainContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Save exam to localStorage
  const saveExamToStorage = (questions) => {
    const examData = {
      questions,
      timestamp: new Date().toISOString(),
      config: examConfig,
      results: calculateResults()
    };
    localStorage.setItem('bellMathsExam', JSON.stringify(examData));
  };

  // Load exam from localStorage
  const loadExamFromStorage = () => {
    const saved = localStorage.getItem('bellMathsExam');
    if (saved) {
      const examData = JSON.parse(saved);
      setExamQuestions(examData.questions);
      setExamConfig(examData.config);
      setExamCompleted(true);
      setShowResults(true);
      setTotalQuestions(examData.questions.length);
    }
  };

  // Calculate results
  const calculateResults = () => {
    const correctAnswers = examQuestions.filter(q => q.isCorrect).length;
    const totalQuestions = examQuestions.length;
    const percentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
    
    return {
      correctAnswers,
      totalQuestions,
      percentage
    };
  };

  // Handle input focus
  const handleInputFocus = (index, type, questionIndex) => {
    setActiveInput({ index, type, questionIndex });
    setKeyboardVisible(true);
    
    const inputElement = document.querySelector(`input[data-question="${questionIndex}"][data-index="${type}-${index}"][data-type="${type}"]`);
    
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      const mathProblemRect = mathProblemRefs.current[questionIndex]?.getBoundingClientRect();
      
      if (mathProblemRect) {
        const question = examQuestions.find(q => q.id === questionIndex);
        const maxLength = Math.max(question.num1.length, question.num2.length);
        const placeValue = maxLength - index - 1;
        
        let x = rect.right - mathProblemRect.left + 5;
        
        if (placeValue >= 1) {
          x -= (placeValue * 15);
        }
        
        x = Math.max(x, 50);
        
        let y = rect.top - mathProblemRect.top;
        
        if (type === 'afterBorrowing' || type === 'afterReduction') {
          y += 60;
        } else {
          y += 40;
        }
        
        setKeyboardPosition({ x, y });
      }
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    // Keep keyboard visible for virtual input
  };

  // Handle virtual keyboard input
  const handleVirtualKeyPress = (key) => {
    if (activeInput) {
      if (activeInput.type === 'answer') {
        handleAnswerChange(activeInput.questionIndex, activeInput.index, key);
      } else if (activeInput.type === 'afterBorrowing') {
        handleAfterBorrowingChange(activeInput.questionIndex, activeInput.index, key);
      } else if (activeInput.type === 'afterReduction') {
        handleAfterReductionChange(activeInput.questionIndex, activeInput.index, key);
      } else if (activeInput.type === 'strikeout') {
        handleStrikeoutChange(activeInput.questionIndex, activeInput.index, key);
      }
      
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-question="${activeInput.questionIndex}"][data-index="${activeInput.type}-${activeInput.index}"]`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle virtual keyboard backspace
  const handleVirtualBackspace = () => {
    if (activeInput) {
      if (activeInput.type === 'answer') {
        handleAnswerChange(activeInput.questionIndex, activeInput.index, '');
      } else if (activeInput.type === 'afterBorrowing') {
        handleAfterBorrowingChange(activeInput.questionIndex, activeInput.index, '');
      } else if (activeInput.type === 'afterReduction') {
        handleAfterReductionChange(activeInput.questionIndex, activeInput.index, '');
      } else if (activeInput.type === 'strikeout') {
        handleStrikeoutChange(activeInput.questionIndex, activeInput.index, '');
      }
      
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-question="${activeInput.questionIndex}"][data-index="${activeInput.type}-${activeInput.index}"]`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle virtual keyboard clear
  const handleVirtualClear = () => {
    handleVirtualBackspace();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, currentIndex, type, questionId) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const inputs = document.querySelectorAll(`input[data-question="${questionId}"][data-index]`);
      const currentInputIndex = Array.from(inputs).findIndex(input => input === e.target);
      
      if (e.shiftKey) {
        if (currentInputIndex > 0) {
          inputs[currentInputIndex - 1].focus();
        }
      } else {
        if (currentInputIndex < inputs.length - 1) {
          inputs[currentInputIndex + 1].focus();
        }
      }
    }
  };

  // Hide keyboard when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.virtual-keyboard') && 
          !event.target.closest('.digit-input') && 
          !event.target.closest('.after-reduction-input') &&
          !event.target.closest('.after-borrowing-input') &&
          !event.target.closest('.strikeout-input')) {
        setKeyboardVisible(false);
        setActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(time => {
          if (time <= 1) {
            // Time's up - auto submit
            submitExam();
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    } else if (timeRemaining === 0 && timerActive) {
      setTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [timerActive]);

  // Load saved exam on component mount
  useEffect(() => {
    loadExamFromStorage();
  }, []);

  // Auto-save progress every 30 seconds
  useEffect(() => {
    if (examStarted && examQuestions.length > 0) {
      const interval = setInterval(() => {
        saveExamToStorage(examQuestions);
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [examQuestions, examStarted]);

  const results = calculateResults();
  const totalPages = Math.ceil(totalQuestions / questionsPerPage);
  const startIndex = (currentPage - 1) * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const currentQuestions = examQuestions.slice(startIndex, endIndex);

  if (!examStarted) {
    return (
      <div className="exam-setup">
        <div className="page-title">
          <h2>Subtraction Exam Setup</h2>
          <p>Configure your subtraction exam settings</p>
        </div>

        <div className="exam-config">
          <div className="config-item">
            <label>Number of Questions:</label>
            <select 
              value={examConfig.questionCount} 
              onChange={(e) => setExamConfig(prev => ({ ...prev, questionCount: parseInt(e.target.value) }))}
            >
              {[1, 2, 5, 10, 15, 20].map(count => (
                <option 
                  key={count} 
                  value={count}
                  disabled={count > 2}
                  style={count > 2 ? { color: '#999', fontStyle: 'italic' } : {}}
                >
                  {count} {count === 1 ? 'Question' : 'Questions'}
                  {count > 2 && ' (Upgrade to Pro Edition)'}
                </option>
              ))}
            </select>
            {examConfig.questionCount > 2 && (
              <div style={{ 
                marginTop: '8px', 
                padding: '8px 12px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeaa7', 
                borderRadius: '4px',
                fontSize: '14px',
                color: '#856404'
              }}>
                ⚠️ Upgrade to Pro Edition to support {examConfig.questionCount} questions
              </div>
            )}
          </div>

          <div className="config-item">
            <label>Level:</label>
            <select 
              value={examConfig.gradeLevel} 
              onChange={(e) => setExamConfig(prev => ({ ...prev, gradeLevel: e.target.value }))}
            >
              {Array.from({ length: 8 }, (_, i) => i + 1).map(level => (
                <option 
                  key={level} 
                  value={level.toString()}
                  disabled={level > 3}
                  style={{ color: level > 3 ? '#999' : 'inherit' }}
                >
                  {level > 3 ? `Level ${level} (Upto ${level + 1} digits) - Upgrade to Pro Edition` : `Level ${level} (Upto ${level + 1} digits)`}
                </option>
              ))}
            </select>
          </div>

          <div className="config-item" style={{ marginTop: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={examConfig.useTimer}
                onChange={(e) => setExamConfig(prev => ({ ...prev, useTimer: e.target.checked }))}
              />
              Enable Timer
            </label>
          </div>

          {examConfig.useTimer && (
            <div className="config-item">
              <label>Time Limit (minutes):</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                <input
                  type="range"
                  min="1"
                  max="180"
                  value={examConfig.timeLimit}
                  onChange={(e) => setExamConfig(prev => ({ ...prev, timeLimit: parseInt(e.target.value) }))}
                  style={{ flex: 1 }}
                />
                <input
                  type="number"
                  min="1"
                  max="180"
                  value={examConfig.timeLimit}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 1 && value <= 180) {
                      setExamConfig(prev => ({ ...prev, timeLimit: value }));
                    }
                  }}
                  style={{ 
                    width: '80px', 
                    padding: '5px', 
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
                <span style={{ fontSize: '14px', color: '#666' }}>
                  {examConfig.timeLimit} min
                </span>
              </div>
            </div>
          )}

          <button className="btn btn-primary btn-large" onClick={generateExam}>
            Start Subtraction Exam
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Exam Header */}
      <div className="exam-header">
        <div className="exam-info">
          <h2>Subtraction Exam - Grade {examConfig.gradeLevel}</h2>
          <p>Question {startIndex + 1} - {Math.min(endIndex, totalQuestions)} of {totalQuestions}</p>
          {examConfig.useTimer && (
            <div style={{ 
              marginTop: '10px',
              padding: '10px 15px',
              backgroundColor: timeRemaining <= 300 ? '#f8d7da' : '#d4edda',
              borderRadius: '6px',
              border: `2px solid ${timeRemaining <= 300 ? '#dc3545' : '#28a745'}`,
              textAlign: 'center'
            }}>
              <strong style={{ 
                color: timeRemaining <= 300 ? '#721c24' : '#155724',
                fontSize: '18px'
              }}>
                Time Remaining: {formatTime(timeRemaining)}
              </strong>
            </div>
          )}
        </div>
        
                 <div className="exam-actions">
          {!examCompleted ? (
            <>
              {/* Before exam submission - show both print buttons */}
              <button className="btn btn-secondary" onClick={printQuestionsOnly}>
                Print BellMaths Notebook
              </button>
              <button className="btn btn-secondary" onClick={printPlainQuestions}>
                Print Questions
              </button>
            </>
          ) : (
            <>
              {/* After exam submission - show both print buttons */}
              <button className="btn btn-secondary" onClick={printExam}>
                Print BellMaths Notebook with Answers
              </button>
              <button className="btn btn-secondary" onClick={printPlainQuestions}>
                Print Questions
              </button>
            </>
          )}
          {/* Submit Exam Button - Moved to top as requested */}
          {!examCompleted && (
            <button className="btn btn-success" onClick={submitExam}>
              Submit Exam
            </button>
          )}
          {/* Restart Exam Button - Fixed scope to stay within subtraction module */}
          {showResults && (
            <button 
              className="btn btn-primary" 
              onClick={() => {
                setExamStarted(false);
                setExamCompleted(false);
                setShowResults(false);
                setExamQuestions([]);
                setCurrentPage(1);
                setKeyboardVisible(false);
                setActiveInput(null);
                setTimeRemaining(0);
                setTimerActive(false);
              }}
              style={{ 
                padding: '10px 20px', 
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              Start New Exam
            </button>
          )}
        </div>
      </div>

      {/* Questions */}
      <div className="questions-container">
        {showResults && (
          <div className="exam-results" style={{ 
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #667eea'
          }}>
            <h3 style={{ color: '#667eea', marginBottom: '15px' }}>Subtraction Exam Results</h3>
            <div className="results-summary" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '15px',
              marginBottom: '20px'
            }}>
              <div className="result-item" style={{ 
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <span className="result-label" style={{ fontWeight: 'bold', color: '#667eea' }}>Correct Answers:</span>
                <span className="result-value" style={{ marginLeft: '10px', fontWeight: 'bold' }}>{results.correctAnswers}/{results.totalQuestions}</span>
              </div>
              <div className="result-item" style={{ 
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <span className="result-label" style={{ fontWeight: 'bold', color: '#667eea' }}>Percentage:</span>
                <span className="result-value" style={{ marginLeft: '10px', fontWeight: 'bold' }}>{results.percentage}%</span>
              </div>
              <div className="result-item" style={{ 
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <span className="result-label" style={{ fontWeight: 'bold', color: '#667eea' }}>Papers Saved:</span>
                <span className="result-value" style={{ marginLeft: '10px', fontWeight: 'bold' }}>{calculatePaperSavings().papersSaved}</span>
              </div>
            </div>
          </div>
        )}

        {/* Question Navigation Bar */}
        {!showResults && examQuestions.length > 0 && (
          <div style={{
            position: 'sticky',
            top: '0',
            zIndex: 100,
            backgroundColor: 'white',
            padding: '15px',
            borderBottom: '2px solid #e9ecef',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <h4 style={{ margin: 0, color: '#667eea' }}>Quick Navigation</h4>
              <span style={{ fontSize: '14px', color: '#666' }}>
                Click any question number to jump to it
              </span>
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              maxHeight: '120px',
              overflowY: 'auto'
            }}>
              {examQuestions.map((question, index) => {
                const hasAnswer = question.userAnswer && question.userAnswer.some(digit => digit);
                return (
                  <button
                    key={question.id}
                    onClick={() => {
                      const questionElement = document.getElementById(`question-${question.id}`);
                      if (questionElement) {
                        questionElement.scrollIntoView({ 
                          behavior: 'smooth', 
                          block: 'center' 
                        });
                      }
                    }}
                    style={{
                      padding: '8px 12px',
                      border: '2px solid',
                      borderColor: hasAnswer ? '#28a745' : '#e9ecef',
                      backgroundColor: hasAnswer ? '#d4edda' : 'white',
                      color: hasAnswer ? '#155724' : '#666',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      minWidth: '40px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.05)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {question.id}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        {currentQuestions.map((question, questionIndex) => {
          const maxLength = Math.max(question.num1.length, question.num2.length);
          
          return (
            <div key={question.id} id={`question-${question.id}`} className="question-container">
              <div className="question-header">
                <h3>Question {question.id}</h3>
                {examCompleted && (
                  <span className={`result-badge ${question.isCorrect ? 'correct' : 'incorrect'}`}>
                    {question.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                )}
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: examCompleted && question.explanation ? '1fr 1fr' : '1fr',
                gap: '20px',
                alignItems: 'start'
              }}>
                {/* Math Problem Section */}
                <div className="math-problem" ref={el => mathProblemRefs.current[question.id] = el}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: `40px repeat(${maxLength}, 40px) auto`,
                    gap: '0px',
                    alignItems: 'center'
                  }}>
                    {/* After Reduction row */}
                    <div></div>
                    {Array.from({ length: maxLength }, (_, index) => (
                      <div key={`after-reduction-${index}`} style={{ textAlign: 'center' }}>
                        {index < maxLength - 1 && (
                          <input
                            type="text"
                            className="after-reduction-input"
                            value={question.afterReduction?.[index] || ''}
                            onChange={(e) => handleAfterReductionChange(question.id, index, e.target.value)}
                            onFocus={() => handleInputFocus(index, 'afterReduction', question.id)}
                            onBlur={handleInputBlur}
                            onKeyDown={(e) => handleKeyDown(e, index, 'afterReduction', question.id)}
                            data-index={`afterReduction-${index}`}
                            data-question={question.id}
                            data-type="afterReduction"
                            placeholder=""
                            maxLength={1}
                            style={{
                              width: '30px',
                              height: '25px',
                              fontSize: '0.8rem',
                              textAlign: 'center',
                              border: 'none',
                              borderBottom: '2px solid #0066cc',
                              borderRadius: '0',
                              backgroundColor: '#e6f3ff',
                              color: '#0066cc',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                      </div>
                    ))}
                    <div style={{ fontSize: '0.8rem', color: '#0066cc', fontWeight: 'bold' }}>
                      After Reduction
                    </div>

                    {/* After Borrowing row */}
                    <div></div>
                    {Array.from({ length: maxLength }, (_, index) => (
                      <div key={`after-borrowing-${index}`} style={{ textAlign: 'center' }}>
                        {index < maxLength && (
                          <input
                            type="text"
                            className="after-borrowing-input"
                            value={question.afterBorrowing?.[index] || ''}
                            onChange={(e) => handleAfterBorrowingChange(question.id, index, e.target.value)}
                            onFocus={() => handleInputFocus(index, 'afterBorrowing', question.id)}
                            onBlur={handleInputBlur}
                            onKeyDown={(e) => handleKeyDown(e, index, 'afterBorrowing', question.id)}
                            data-index={`afterBorrowing-${index}`}
                            data-question={question.id}
                            data-type="afterBorrowing"
                            placeholder=""
                            maxLength={2}
                            style={{
                              width: '35px',
                              height: '25px',
                              fontSize: '0.8rem',
                              textAlign: 'center',
                              border: 'none',
                              borderBottom: '2px solid #ff8c00',
                              borderRadius: '0',
                              backgroundColor: '#ffe6cc',
                              color: '#ff8c00',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                      </div>
                    ))}
                    <div style={{ fontSize: '0.8rem', color: '#ff8c00', fontWeight: 'bold' }}>
                      After Borrowing
                    </div>

                    {/* First number row with strikeout functionality */}
                    <div></div>
                    {question.num1.padStart(maxLength, ' ').split('').map((digit, index) => (
                     <div key={`num1-container-${index}`} style={{ 
                       width: '40px', 
                       height: '50px',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       position: 'relative'
                     }}>
                       <input
                         type="text"
                         className="digit-input"
                         value={digit === ' ' ? '' : digit}
                         readOnly
                         style={{ 
                           backgroundColor: '#e8f4fd',
                           width: '40px',
                           height: '50px',
                           textAlign: 'center',
                           fontSize: '1.5rem',
                           fontWeight: 'bold',
                           border: '2px solid #ddd',
                           borderRadius: '4px',
                           position: 'relative',
                           zIndex: 1
                         }}
                       />
                       {/* Strikeout overlay */}
                       {question.strikeout?.[index] === '/' && (
                         <div style={{
                           position: 'absolute',
                           top: '50%',
                           left: '50%',
                           transform: 'translate(-50%, -50%)',
                           width: '45px',
                           height: '3px',
                           backgroundColor: '#dc3545',
                           transform: 'translate(-50%, -50%) rotate(-45deg)',
                           zIndex: 2,
                           pointerEvents: 'none'
                         }}></div>
                       )}
                       {/* Strikeout input */}
                       <input
                         type="text"
                         className="strikeout-input"
                         value={question.strikeout?.[index] || ''}
                         onChange={(e) => handleStrikeoutChange(question.id, index, e.target.value)}
                         onFocus={() => handleInputFocus(index, 'strikeout', question.id)}
                         onBlur={handleInputBlur}
                         onKeyDown={(e) => handleKeyDown(e, index, 'strikeout', question.id)}
                         data-index={`strikeout-${index}`}
                         data-question={question.id}
                         data-type="strikeout"
                         placeholder=""
                         maxLength={1}
                         style={{
                           position: 'absolute',
                           top: '2px',
                           right: '2px',
                           width: '15px',
                           height: '15px',
                           fontSize: '0.6rem',
                           textAlign: 'center',
                           border: '1px solid #dc3545',
                           borderRadius: '2px',
                           backgroundColor: '#f8d7da',
                           color: '#dc3545',
                           zIndex: 3
                         }}
                       />
                     </div>
                   ))}
                   <div></div>

                    {/* Second number row with operator */}
                    <span className="operator" style={{ textAlign: 'center' }}>-</span>
                    {question.num2.padStart(maxLength, ' ').split('').map((digit, index) => (
                     <input
                       key={`num2-${index}`}
                       type="text"
                       className="digit-input"
                       value={digit === ' ' ? '' : digit}
                       readOnly
                       style={{ 
                         backgroundColor: '#e8f4fd',
                         width: '40px',
                         height: '50px',
                         textAlign: 'center',
                         fontSize: '1.5rem',
                         fontWeight: 'bold',
                         border: '2px solid #ddd',
                         borderRadius: '4px'
                       }}
                     />
                   ))}
                   <div></div>

                    {/* Line row */}
                    <div></div>
                    {Array.from({ length: maxLength }, (_, index) => (
                     <div key={`line-${index}`} style={{ 
                       height: '2px', 
                       backgroundColor: '#333',
                       margin: '10px 0'
                     }}></div>
                   ))}
                   <div></div>

                    {/* Answer row */}
                    <div></div>
                    {Array.from({ length: maxLength }, (_, index) => (
                      <div key={`answer-container-${index}`} style={{ 
                        width: '40px', 
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                      }}>
                        <input
                          type="text"
                          className={`digit-input ${question.isCorrect !== null ? 
                            (question.userAnswer?.[index] === question.correctAnswer[index] ? 'correct' : 'incorrect') : ''}`}
                          value={question.userAnswer?.[index] || ''}
                          onChange={(e) => handleAnswerChange(question.id, index, e.target.value)}
                          onFocus={() => handleInputFocus(index, 'answer', question.id)}
                          onBlur={handleInputBlur}
                          onKeyDown={(e) => handleKeyDown(e, index, 'answer', question.id)}
                          data-index={`answer-${index}`}
                          data-question={question.id}
                          data-type="answer"
                          maxLength={1}
                          placeholder=""
                          style={{
                            width: '20px',
                            height: '30px',
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            border: 'none',
                            borderRadius: '0',
                            backgroundColor: 'transparent',
                            transition: 'border-color 0.3s ease',
                            outline: 'none'
                          }}
                        />
                        {/* Dash underline */}
                        <div style={{
                          position: 'absolute',
                          bottom: '5px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '25px',
                          height: '3px',
                          backgroundColor: '#333',
                          borderRadius: '1px'
                        }}></div>
                      </div>
                    ))}
                    <div></div>
                   
                    {/* Virtual Keyboard */}
                    {activeInput && activeInput.questionIndex === question.id && keyboardVisible && (
                      <VirtualKeyboard
                        onKeyPress={handleVirtualKeyPress}
                        onBackspace={handleVirtualBackspace}
                        onClear={handleVirtualClear}
                        position={keyboardPosition}
                        isVisible={keyboardVisible}
                      />
                    )}
                  </div>
                </div>

                {/* Step-by-Step Solution Section - Side by Side */}
                {examCompleted && question.explanation && (
                  <div className="solution-section">
                    <div style={{ marginBottom: '10px' }}>
                      <h4>Step-by-Step Solution:</h4>
                    </div>
                    <div className="explanation-steps" style={{ fontSize: '14px' }}>
                      {question.explanation.steps.map((step, stepIndex) => (
                        <div key={stepIndex} style={{ 
                          marginBottom: '12px', 
                          padding: '10px', 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: '6px', 
                          borderLeft: '4px solid #667eea',
                          fontSize: '13px'
                        }}>
                          <div style={{ marginBottom: '6px' }}>
                            <strong style={{ color: '#667eea', fontSize: '14px' }}>Step {stepIndex + 1}: {step.placeValue} place</strong>
                          </div>
                          <div style={{ marginBottom: '4px', fontFamily: 'monospace', fontSize: '12px', color: '#495057' }}>
                            <strong>Equation:</strong> {step.equation}
                          </div>
                          <div style={{ marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                            <strong>Explanation:</strong> {step.explanation}
                          </div>
                          {step.needsBorrow && (
                            <div style={{ 
                              marginTop: '4px', 
                              padding: '4px', 
                              backgroundColor: '#fff3cd', 
                              borderRadius: '3px', 
                              fontSize: '11px', 
                              color: '#856404' 
                            }}>
                              <strong>Borrowing:</strong> We borrowed 1 from the next place value ({step.newBorrow} will be carried to the next step)
                            </div>
                          )}
                        </div>
                      ))}
                      <div style={{ 
                        marginTop: '8px', 
                        padding: '6px', 
                        backgroundColor: '#d4edda', 
                        borderRadius: '4px', 
                        border: '1px solid #c3e6cb',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: '#155724',
                        fontSize: '13px'
                      }}>
                        <strong>Answer: {question.correctAnswer}</strong>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Submit Button */}
      {!examCompleted && (
        <div style={{ 
          textAlign: 'center', 
          marginTop: '30px', 
          padding: '20px',
          borderTop: '2px solid #e9ecef'
        }}>
          <button className="btn btn-success btn-large" onClick={submitExam}>
            Submit Exam
          </button>
        </div>
      )}

      {/* Pagination */}
      <div className="exam-pagination">
        <button 
          className="btn btn-secondary" 
          onClick={() => {
            setCurrentPage(prev => Math.max(1, prev - 1));
            // Scroll to top when navigating
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        
        <span className="page-info">
          Page {currentPage} of {totalPages}
        </span>
        
        <button 
          className="btn btn-secondary" 
          onClick={() => {
            setCurrentPage(prev => Math.min(totalPages, prev + 1));
            // Scroll to top when navigating
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>


    </>
  );
};

export default SubtractionExam; 